import React, { useState, useRef } from "react";
import env from "../../../../../config/env";
import { Drawer, Form, Input, Select, Button, message, DatePicker } from "antd";
import { UploadOutlined, CloseOutlined } from "@ant-design/icons";
import db from "../../../../../database";
import useAuthStore from "../../../../../stores/useAuthStore";

const { Option } = Select;

const BankTransferDrawer = ({ open, onClose, context, selectedPaymentMethod, onSubmit, amount, pricePrecision }) => {
  const [form] = Form.useForm();
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const fileInputRef = useRef(null);
  const tillData = useAuthStore.getState().tillData;

  const loadFinancialAccounts = async () => {
    if (financialAccounts.length > 0) return;
    const accounts = await db?.getFinancialAccounts?.toArray();
    setFinancialAccounts(accounts || []);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let fileToUpload = file;
      if (file.size > 4 * 1024 * 1024 && file.type.startsWith("image/")) {
        fileToUpload = await compressImage(file, 1920, 0.75);
      }
      const buffer = await fileToUpload.arrayBuffer();
      const fileData = Array.from(new Uint8Array(buffer));
      const response = await fetch(env.uploadDocumentUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fileName: fileToUpload.name, fileType: fileToUpload.type, fileData }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result?.error || "Upload failed");
      setUploadedFileUrl(result.url);
      setUploadedFileName(file.name);
      message.success("Document uploaded successfully.");
    } catch (error) {
      console.error("Upload error:", error);
      message.error("Failed to upload document.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const image = new Image();
        image.src = reader.result;
        image.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          let { width, height } = image;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(image, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Compression failed"));
                return;
              }
              resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
            },
            file.type,
            quality
          );
        };
        image.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const bankTransferData = {
          isBankTransfer: "Y",
          amount: values.amount ? parseFloat(values.amount) : null,
          financialAccount: values.financialAccountId || null,
          transactionId: values.transactionId || null,
          transactionDate: values.transactionDate || null,
          financialFile: uploadedFileUrl || null,
        };
        onSubmit(bankTransferData);
        resetAndClose();
      })
      .catch(() => {});
  };

  const resetAndClose = () => {
    form.resetFields();
    setUploadedFileUrl(null);
    setUploadedFileName(null);
    onClose();
  };

  const contextLabel = context?.type === "wallettopup" ? "Wallet Top-Up" : context?.type === "walletrefund" ? "Wallet Refund" : "Payment";
  const currSymbol = tillData?.tillAccess?.csBunit?.currencies?.[0]?.currSymbol || "";

  return (
    <Drawer
      open={open}
      title={
        <span style={{ color: "#000", fontWeight: 600 }}>
          {`Bank Transfer - ${contextLabel}`}
        </span>
      }
      placement="right"
      width={420}
      onClose={resetAndClose}
      closable={false}
      extra={<CloseOutlined onClick={resetAndClose} style={{ cursor: "pointer", fontSize: "16px" }} />}
      styles={{ body: { padding: "20px", color: "#2F3856" } }}
      headerStyle={{ background: "white", borderBottom: "none", color: "#000" }}
    >
      <Form form={form} layout="vertical" initialValues={{ amount: amount ? parseFloat(amount).toFixed(pricePrecision || 2) : "" }}>
        <Form.Item
          name="amount"
          label={`Amount (${currSymbol})`}
          rules={[
            { required: true, message: "Please enter the amount" },
            {
              validator: (_, val) => {
                const isWalletFlow = context?.type === "wallettopup" || context?.type === "walletrefund";
                if (isWalletFlow && amount && parseFloat(val) > parseFloat(amount)) {
                  return Promise.reject(new Error(`Amount cannot exceed ${parseFloat(amount).toFixed(pricePrecision || 2)}`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input type="number" placeholder="Enter amount" style={{ height: "44px", fontSize: "18px", fontWeight: 600 }} min={0} />
        </Form.Item>

        <Form.Item name="financialAccountId" label="Bank Account" rules={[{ required: true, message: "Please select a bank account" }]}>
          <Select placeholder="Select Bank Account" onClick={loadFinancialAccounts} style={{ height: "44px" }}>
            {financialAccounts.map((acct) => (
              <Option key={acct.fin_financial_acct_id} value={acct.fin_financial_acct_id}>
                {acct.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="transactionId" label="Transaction / Reference ID" rules={[{ required: true, message: "Please enter the transaction ID" }]}>
          <Input placeholder="Enter transaction reference" style={{ height: "44px" }} />
        </Form.Item>

        <Form.Item name="transactionDate" label="Transaction Date">
          <DatePicker style={{ width: "100%", height: "44px" }} />
        </Form.Item>

        <Form.Item label="Upload Receipt / Document">
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: "none" }} onChange={handleFileUpload} />
          <Button icon={<UploadOutlined />} loading={uploading} onClick={() => fileInputRef.current?.click()} style={{ width: "100%" }}>
            {uploadedFileName || "Choose File"}
          </Button>
          {uploadedFileUrl && <div style={{ marginTop: "8px", fontSize: "12px", color: "#16a34a" }}>File uploaded successfully</div>}
        </Form.Item>
      </Form>

      <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
        <Button onClick={resetAndClose} style={{ flex: 1, height: "44px", borderRadius: "6px" }}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit} style={{ flex: 1, height: "44px", borderRadius: "6px", background: "#2F3856" }}>
          Confirm Payment
        </Button>
      </div>
    </Drawer>
  );
};

export default BankTransferDrawer;
